import {
    PanelType,
    PriceType,
    Project,
    PROJECT_STATUS
} from '@nexxt/common/types';
import { currencyFormatter } from '@nexxt/common/utils/index';
import { isNull, isNumber } from 'lodash';
import * as React from 'react';
import { CHECKOUT } from '../../../../../constants/routes';
import {
    getAvailableProjects,
    getTenantLevel
} from '@nexxt/common/firebase/auth/userDetails';
import { interpolate } from '../../../../../utils/string';
import { updatePanelProject } from '../../../../../services/sample.service';
import { ILocation } from '../../../../../interface/common.interface';
import { PROJECT_BASE_COST } from '../../../../../constants/project';
interface PriceDetailProps {
    currentProject?: Project;
    isPriceModal: boolean;
    type: any;
    devMode?: boolean;
    totalQuestionCount: number;
    onClose?: () => void;
    changePath?: (path: string | ILocation) => void;
    setToggleIncidenceRateModal?: () => void;
    launchSandbox?: () => void;
    orderReview?: any;
}

const PriceDetail: React.FunctionComponent<PriceDetailProps> = (
    props: PriceDetailProps
) => {
    const {
        isPriceModal,
        type,
        currentProject,
        onClose,
        changePath,
        setToggleIncidenceRateModal,
        orderReview
    } = props;
    const priceType = type.PRICE_MODE;
    const availableProjects = getAvailableProjects();
    const tenantLevel = getTenantLevel();
    const countryCurrency = 'USD';
    let totalPrice = parseFloat(type.price) || 0;
    const customPricing =
        ![PriceType.REGULAR, PriceType.FREE].includes(priceType) && !totalPrice;

    const audienceCost = customPricing
        ? type.label
        : type.audiencePrice
        ? parseFloat(type.audiencePrice)
        : 0;

    if (customPricing) {
        totalPrice = type.label;
    }
    const requiredProjectCredits =
        currentProject?.status == PROJECT_STATUS.COMPLETE ||
        currentProject?.status == PROJECT_STATUS.DRAFT
            ? '1'
            : '0';
    const isNotFeasible = priceType === PriceType.NOT_FEASIBLE;
    const guaranteedNum =
        isNotFeasible &&
        currentProject?.panelSettings?.dynata?.price?.promisedCount;
    const hasLaunched = currentProject?.status !== PROJECT_STATUS.DRAFT;
    const proceedCheckout = async () => {
        isNotFeasible &&
            (await updatePanelProject(currentProject, { sampleSize: true }));
        changePath({
            pathname: interpolate(CHECKOUT, {
                projectId: props.currentProject.id
            }),
            search: props.devMode ? '?mode=dev' : ''
        } as ILocation);
    };
    const isFullService = tenantLevel === 2;
    /**
     * Add launchSandbox functionality here from ProjectLaunch line 226.
     * If isSandbox true then Button request review will be clicked. Launch sandbox function is called
     */

    //  price detail props
    if (isFullService) {
        return (
            <div className="w-full h-full flex flex-col">
                {isPriceModal && (
                    <>
                        <p className="price--modal--title">
                            Project Cost {isNotFeasible && '(Not feasible)'}
                        </p>
                    </>
                )}
                <div className="price--modal--price-details">
                    <div className="price--modal--price-details-row">
                        <p className="text-lg font-semibold m-0">
                            Platform Cost
                        </p>
                        <p className="text-md font-semibold m-0">
                            {totalPrice
                                ? currencyFormatter(totalPrice, countryCurrency)
                                : 'Free'}
                        </p>
                    </div>
                </div>
                <hr />
                <div className="price--modal--price-details-row">
                    <div>
                        <p className="price--modal--price-details-subtitle">
                            Estimated Tax
                        </p>
                    </div>
                    <p className="text-md font-semibold m-0">
                        {isNumber(totalPrice)
                            ? currencyFormatter(
                                  totalPrice * 0.13,
                                  countryCurrency
                              )
                            : totalPrice}
                    </p>
                </div>
                <div className="price--modal--price-details-row">
                    <div>
                        <span className="price--modal--title">Total</span>
                    </div>
                    <span className="text-md font-semibold m-0">
                        {isNumber(totalPrice)
                            ? currencyFormatter(
                                  totalPrice * 1.13,
                                  countryCurrency
                              )
                            : totalPrice}
                    </span>
                </div>
                {isPriceModal && (
                    <div className="price--modal--footer">
                        <button
                            className="btn btn--sm btn--orange mr-2"
                            onClick={onClose}
                        >
                            Back
                        </button>
                        {currentProject.incidenceRate && (
                            <button
                                className="btn btn--sm btn--yellow mr-2"
                                onClick={() => setToggleIncidenceRateModal()}
                                disabled={!currentProject.incidenceRate}
                            >
                                Edit Incidence
                            </button>
                        )}
                        <button
                            className="btn btn--sm btn--green"
                            disabled={!props.totalQuestionCount}
                            onClick={proceedCheckout}
                        >
                            {isNotFeasible
                                ? 'Proceed to Checkout Anyway'
                                : 'Proceed to Checkout'}
                        </button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col gap-2">
            <div className="checkout--container--column-title">
                Order Review
            </div>
            <div className="flex flex-col w-full gap-2">
                <div className="flex text-xs text-gray-500">
                    <div className="flex-1"></div>
                    <div className="flex gap-2 items-end">
                        <div className="w-12 text-right">
                            Quoted
                            <br />
                            Credits
                        </div>
                        <span>/</span>
                        <div className="w-12 text-right">
                            Available
                            <br />
                            Credits
                        </div>
                    </div>
                </div>
                <div className="flex text-lg font-semibold items-start">
                    <div className="flex-1">
                        <span>
                            Project Cost {isNotFeasible ? '(Not feasible)' : ''}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <div className="w-12 text-right">
                            {requiredProjectCredits}
                        </div>
                        <span>/</span>
                        <div className="w-12 text-right">
                            {availableProjects}
                        </div>
                    </div>
                </div>
                {currentProject.panelSettings?.panelType == PanelType.INCA ? (
                    <>
                        <div className="flex text-lg font-semibold items-start">
                            <div className="flex-1">
                                <span>
                                    {`Audience Cost*${
                                        isPriceModal && isNotFeasible ? '*' : ''
                                    }`}
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <div className="w-32 text-right">
                                    {isNumber(audienceCost)
                                        ? currencyFormatter(
                                              audienceCost,
                                              countryCurrency
                                          )
                                        : audienceCost}
                                </div>
                            </div>
                        </div>
                        {orderReview?.estimatedDateStr && (
                            <div className="flex text-sm items-start -mt-2">
                                <div className="flex-1 flex flex-col">
                                    <span>Estimated completion date</span>
                                </div>
                                <div className="flex gap-2">
                                    <div className="text-right">
                                        {orderReview?.estimatedDateStr}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>

            <hr className="mt-auto" />
            <div className="flex text-sm items-start">
                <div className="flex-1 flex flex-col">
                    <span>Estimated Tax</span>
                </div>
                <div className="flex gap-2">
                    <div className="text-right">
                        {/** Only show tax only after payment intent is created, otherwise shows '-' */}
                        {type?.estimatedTax
                            ? currencyFormatter(
                                  type?.estimatedTax,
                                  countryCurrency
                              )
                            : 'Determined on invoicing'}
                    </div>
                </div>
            </div>
            <div className="flex text-lg font-semibold items-start">
                <div className="flex-1">
                    <span>Total</span>
                </div>
                <div className="flex gap-2">
                    <div className="w-32 text-right">
                        {/** totalPrice is only available after payment intent is created.  */}
                        {type?.totalPrice
                            ? type.totalPrice
                            : isNumber(totalPrice)
                            ? currencyFormatter(totalPrice, countryCurrency)
                            : totalPrice}
                    </div>
                </div>
            </div>
            {audienceCost ? (
                <p className="text-xs text-gray-500">
                    *Estimated cost of audience which may vary from actual
                    invoiced amount depending on incidence rate and other
                    factors.{' '}
                    {isNotFeasible
                        ? `Currently we can guarantee ${guaranteedNum} participants with the current targets which may change slightly during fieldwork.`
                        : ''}
                </p>
            ) : null}
            <p className="text-xs text-gray-500">
                All prices listed in USD. For invoicing in different currencies,
                please contact us.
            </p>

            {isPriceModal && (
                <div className="price--modal--footer">
                    <button
                        className="btn btn--sm btn--orange mr-2"
                        onClick={onClose}
                    >
                        Back
                    </button>
                    {currentProject.incidenceRate && (
                        <button
                            className="btn btn--sm btn--yellow mr-2"
                            onClick={() => setToggleIncidenceRateModal()}
                            disabled={!currentProject.incidenceRate}
                        >
                            Edit Incidence
                        </button>
                    )}
                    <button
                        className="btn btn--sm btn--green"
                        onClick={proceedCheckout}
                        data-test="proceed-to-checkout-modal"
                        disabled={
                            props.currentProject.status ===
                                PROJECT_STATUS.LIVE || !props.totalQuestionCount
                        }
                    >
                        {isNotFeasible
                            ? 'Proceed to Checkout Anyway'
                            : 'Proceed to Checkout'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default PriceDetail;
